'use client';

import { useIsMutating, useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import type { ConfigFormData } from 'shared/types/configuration-service';
import { LoadingButton } from 'shared/ui/loading-button';
import { useApiStatusToast } from 'shared/hooks/useApiStatusToast';
import { postProjectConfig } from '@/enteties/config/api';
import { Config } from 'features/change-config/ui/config';
import { BuildProject } from 'features/build-project';
import { singleProject } from '@/enteties/project/api';
import { useQueryWithOnError } from 'shared/hooks/query/useQueryWithOnError';
// eslint-disable-next-line import/no-cycle
import { SkeletonConfig } from 'features/change-config';
import * as m from '@paraglide/messages';
import { mutationProjectKeys } from '@/enteties/project/api/projects.query-key';
interface ConfigFormProps {
  slug: string;
  children: ReactNode;
}
export const ConfigForm = ({
  slug,
  children
}: ConfigFormProps) => {
  const form = useFormContext();
  const [success, error] = useApiStatusToast();
  const {
    data: project,
    isSuccess
  } = useQueryWithOnError({
    ...singleProject.getSingleProjectQueryOptions({
      slug
    }),
    staleTime: 10 * 60 * 1000
  });
  const isMutating = useIsMutating({
    mutationKey: mutationProjectKeys.validateProject(slug)
  });
  const {
    mutate,
    isPending
  } = useMutation({
    mutationFn: ({
      slug,
      config
    }: {
      slug: string;
      config: ConfigFormData;
    }) => postProjectConfig(slug, config),
    onSuccess: () => {
      success({
        title: m.tab_config_field_change_success()
      });
    },
    onError: e => error({
      description: e.message
    })
  });
  const onSubmit = async (data: ConfigFormData) => {
    mutate({
      slug,
      config: data
    });
  };
  if (!isSuccess) return <SkeletonConfig />;
  return <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-2xl" data-sentry-component="ConfigForm" data-sentry-source-file="config-form.tsx">
      <Config slug={slug} data-sentry-element="Config" data-sentry-source-file="config-form.tsx" />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 my-4">
        <div className="grid grid-cols-2 gap-3 w-full">
          <LoadingButton loading={isPending || !!isMutating} type="submit" size="sm" className="w-full" data-sentry-element="LoadingButton" data-sentry-source-file="config-form.tsx">
            {m.btn_apply()}
          </LoadingButton>
          <BuildProject notDisabled type="button" slug={slug} project={project} buttonChild={m.btn_build()} className="w-full" size="sm" data-sentry-element="BuildProject" data-sentry-source-file="config-form.tsx" />
        </div>
        <div className="flex w-full flex-col md:flex-row md:justify-end">
          {children}
        </div>
      </div>
    </form>;
};